import { useCallback, useRef, useState, useEffect } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 0.05s 8kHz mono silent WAV as a data URL — used to unlock the persistent <audio>
// element under the user's first gesture (Brave/Safari strict autoplay fix).
const SILENT_WAV = "data:audio/wav;base64,UklGRiQEAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAEAAA" + "A".repeat(530);

/**
 * useTTS — Brave-resilient text-to-speech orchestrator for Papaya.
 *
 * Two playback paths attempted in order:
 *   A. Persistent HTMLAudioElement (Brave-friendly — bypasses AudioContext farbling)
 *   B. Web Audio API decode + BufferSource (best fidelity when allowed)
 *
 * NOTE: The browser-native speechSynthesis fallback was REMOVED in iter-33 because
 * Brave's stock voice clashed badly with Papaya's persona. If both paths fail we now
 * surface a "tap to hear Papaya" banner instead — silence is better than the wrong voice.
 *
 * Returns a stable API the chat component binds to its <audio> element and buttons.
 *
 * Usage:
 *   const tts = useTTS({ enabled });
 *   <audio ref={tts.audioRef} ... />
 *   tts.unlockAudio();   // call in send-button click handler
 *   tts.playTTS(textOrBase64, isBase64?);
 *   tts.needsAudioTap — UI flag for the "tap to hear" banner
 *   tts.resumeBlockedAudio() — for the "tap to hear Papaya" banner
 */
export default function useTTS({ enabled }) {
  const [needsAudioTap, setNeedsAudioTap] = useState(false);
  const [loading, setLoading] = useState(false);

  const audioRef = useRef(null);                // bound to the persistent <audio> element
  const audioCtxRef = useRef(null);             // Web Audio API context
  const activeSourceRef = useRef(null);         // current AudioBufferSourceNode (so we can stop)
  const audioUnlockedRef = useRef(false);
  const webAudioBrokenRef = useRef(false);      // Brave farbling — disable after first failure

  // Always read the latest `enabled` value without re-creating callbacks.
  // When `enabled` flips OFF we proactively dismiss the "tap to hear voice" banner
  // so the user doesn't see a stale prompt for audio they've intentionally muted.
  const enabledRef = useRef(enabled);
  useEffect(() => {
    enabledRef.current = enabled;
    if (!enabled) setNeedsAudioTap(false);
  }, [enabled]);

  const unlockAudio = useCallback(() => {
    if (audioUnlockedRef.current) return;
    // 1) Resume an AudioContext
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
        if (audioCtxRef.current.state === 'suspended') {
          audioCtxRef.current.resume().catch(() => { /* expected when Brave blocks before gesture */ });
        }
      }
    } catch (_e) { /* AudioContext unavailable — speech fallback will engage */ }
    // 2) Prime the persistent <audio> element with a silent WAV inside the gesture
    try {
      const el = audioRef.current;
      if (el) {
        el.muted = true;
        el.src = SILENT_WAV;
        const p = el.play();
        if (p && typeof p.then === 'function') {
          p.then(() => {
            audioUnlockedRef.current = true;
            setNeedsAudioTap(false);
            el.muted = false;
          }).catch((err) => { console.debug('[useTTS] silent-prime blocked (Brave?):', err?.name); });
        } else {
          audioUnlockedRef.current = true;
          setNeedsAudioTap(false);
          el.muted = false;
        }
      }
    } catch (_e) { /* persistent <audio> not yet mounted */ }
    // Note: speechSynthesis fallback removed in iter-33 — Brave's stock voice was
    // replacing Papaya. Silence > wrong voice.
  }, []);

  // Helper: base64 → Uint8Array
  const _b64ToBytes = (b64) => {
    const byteChars = atob(b64);
    const arr = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) arr[i] = byteChars.charCodeAt(i);
    return arr;
  };

  // Try Path A — persistent <audio>. Returns true on success.
  const _tryHtmlAudio = useCallback(async (audioData) => {
    const el = audioRef.current;
    if (!el || !audioData) return false;
    try {
      const blob = new Blob([_b64ToBytes(audioData)], { type: 'audio/mpeg' });
      const blobUrl = URL.createObjectURL(blob);
      if (el.__lastBlob) {
        try { URL.revokeObjectURL(el.__lastBlob); } catch (_e) { /* already revoked */ }
      }
      el.__lastBlob = blobUrl;
      el.muted = false;
      el.src = blobUrl;
      el.currentTime = 0;
      const p = el.play();
      if (p && typeof p.then === 'function') {
        await p;
      }
      audioUnlockedRef.current = true;
      setNeedsAudioTap(false);
      return true;
    } catch (err) {
      console.warn('[useTTS] HTMLAudio path failed:', err?.name || err?.message);
      return false;
    }
  }, []);

  // Try Path B — Web Audio API. Returns true on success.
  const _tryWebAudio = useCallback(async (audioData) => {
    if (!audioData || webAudioBrokenRef.current) return false;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return false;
      if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') {
        try { await ctx.resume(); } catch (err) { console.debug('[useTTS] AudioContext.resume rejected:', err?.name); }
      }
      const ab = _b64ToBytes(audioData).buffer;
      const decoded = await new Promise((resolve, reject) => {
        try {
          const p = ctx.decodeAudioData(ab.slice(0), resolve, reject);
          if (p && typeof p.then === 'function') p.then(resolve, reject);
        } catch (e) { reject(e); }
      });
      if (activeSourceRef.current) {
        try { activeSourceRef.current.stop(); } catch (_e) { /* already stopped */ }
      }
      const source = ctx.createBufferSource();
      source.buffer = decoded;
      source.connect(ctx.destination);
      source.start(0);
      activeSourceRef.current = source;
      setNeedsAudioTap(false);
      return true;
    } catch (err) {
      // Brave farbled audio decoding — disable this path for the rest of the session
      console.warn('[useTTS] Web Audio failed (likely Brave farbling), disabling:', err?.message);
      webAudioBrokenRef.current = true;
      return false;
    }
  }, []);

  /**
   * Main entry point. Accepts either text (will be sent to /api/tts) OR base64 audio data.
   * If both playback paths fail, surfaces the "tap to hear Papaya" banner so the user
   * can manually unlock audio. The browser-native speechSynthesis fallback was REMOVED
   * in iter-33 — Brave's stock voice clashed with Papaya's persona.
   */
  const playTTS = useCallback(async (audioB64OrText, isBase64 = false) => {
    if (!enabledRef.current) return;
    // Defensive: silence any Brave-initiated speechSynthesis utterance BEFORE
    // we play our own audio — prevents the Brave voice from talking over Papaya.
    try {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    } catch (_e) { /* speechSynthesis disabled */ }
    const text = !isBase64 ? audioB64OrText : null;
    let audioData = isBase64 ? audioB64OrText : null;

    try {
      // Fetch TTS audio if we only have text
      if (!audioData && text) {
        setLoading(true);
        const res = await fetch(`${API}/tts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: text.substring(0, 4096) }),
          cache: 'no-store',
        });
        if (res.ok) {
          const data = await res.json();
          audioData = data.audio;
        }
        setLoading(false);
      }

      if (audioData && (await _tryHtmlAudio(audioData))) return;
      if (audioData && (await _tryWebAudio(audioData))) return;

      // Both playback paths failed — surface the manual-unlock banner.
      // We do NOT fall back to speechSynthesis (Brave replaces Papaya's voice with its own).
      setNeedsAudioTap(true);
    } catch (err) {
      console.warn('[useTTS] All playback paths failed:', err?.message);
      setLoading(false);
      setNeedsAudioTap(true);
    }
  }, [_tryHtmlAudio, _tryWebAudio]);

  // Tapped by the "your browser blocked voice" banner
  const resumeBlockedAudio = useCallback(() => {
    audioUnlockedRef.current = true;
    setNeedsAudioTap(false);
    const el = audioRef.current;
    if (el) {
      try { el.play().catch((err) => console.debug('[useTTS] resume blocked:', err?.name)); }
      catch (_e) { /* play() unavailable */ }
    }
  }, []);

  // Imperative stop — used when the user toggles voice OFF or closes the chat.
  // Also revokes the in-flight blob URL so the browser can reclaim its memory.
  const stop = useCallback(() => {
    try {
      const el = audioRef.current;
      if (el) {
        el.pause();
        if (el.__lastBlob) {
          try { URL.revokeObjectURL(el.__lastBlob); } catch (_e) { /* already revoked */ }
          el.__lastBlob = null;
        }
      }
    } catch (_e) { /* audio element not mounted */ }
    try {
      if (activeSourceRef.current) activeSourceRef.current.stop();
    } catch (_e) { /* already stopped */ }
    try {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    } catch (_e) { /* speechSynthesis disabled */ }
  }, []);

  // Cleanup on unmount — guarantees no blob URLs leak after a forced unmount mid-playback
  useEffect(() => {
    return () => {
      try {
        const el = audioRef.current;
        if (el && el.__lastBlob) {
          URL.revokeObjectURL(el.__lastBlob);
          el.__lastBlob = null;
        }
      } catch (_e) { /* element already unmounted */ }
      try {
        if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
          window.speechSynthesis.cancel();
        }
      } catch (_e) { /* speechSynthesis disabled */ }
    };
  }, []);

  return {
    audioRef,
    unlockAudio,
    playTTS,
    resumeBlockedAudio,
    stop,
    needsAudioTap,
    loading,
  };
}
